// The same GA4 property used by serviti.ai. Google Ads imports the `sign_up`
// key event from this property; do not invent a separate AW-* destination here.
export const GOOGLE_TAG_ID = 'G-T3R4YZZHC8';

const sendEvent = (name, parameters = {}) => {
  if (
    typeof window === 'undefined'
    || window.location.hostname !== 'app.serviti.ai'
    || typeof window.gtag !== 'function'
  ) return false;
  window.gtag('event', name, parameters);
  return true;
};

const sendOncePerSession = (storageKey, name, parameters = {}) => {
  try {
    if (window.sessionStorage.getItem(storageKey) === '1') return false;
  } catch (_) {
    // Analytics must never block signup in hardened/private browsers.
  }

  const queued = sendEvent(name, parameters);
  if (queued) {
    try {
      window.sessionStorage.setItem(storageKey, '1');
    } catch (_) {
      // The event was queued; unavailable storage only removes deduplication.
    }
  }
  return queued;
};

// What Google may see of an app address. Workspace and record ids become
// placeholders (they identify customers and group nothing in reports), and the
// query string keeps only campaign tags: others can carry personal data, such
// as the ?email= the "no account yet" emails link with.
const CAMPAIGN_PARAMS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'gbraid', 'wbraid',
];
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

export const cleanPageLocation = (href) => {
  let url;
  try {
    url = new URL(href);
  } catch (_) {
    return '';
  }
  const path = url.pathname
    .replace(/^\/t\/[^/]+/, '/t/:workspace')
    .replace(UUID, ':id');
  const kept = new URLSearchParams();
  CAMPAIGN_PARAMS.forEach((name) => {
    if (url.searchParams.has(name)) kept.set(name, url.searchParams.get(name));
  });
  const query = kept.toString();
  return `${url.origin}${path}${query ? `?${query}` : ''}`;
};

// One page view per app page. Also makes the cleaned address, and the cleaned
// page before it, the ones every later event reports, so no event carries a
// raw URL. `referrer` is that page before: the previous app page after a move
// inside the app, else the browser's referrer -- which, after a full page load
// inside the app (sign-in, a payment return), is a raw app address too.
export const trackPageView = (href = window.location.href, title = document.title, referrer = document.referrer) => {
  if (
    typeof window === 'undefined'
    || window.location.hostname !== 'app.serviti.ai'
    || typeof window.gtag !== 'function'
  ) return false;
  const pageLocation = cleanPageLocation(href);
  if (!pageLocation) return false;
  const pageReferrer = referrer ? cleanPageLocation(referrer) : '';
  const page = { page_location: pageLocation, ...(pageReferrer ? { page_referrer: pageReferrer } : {}) };
  window.gtag('set', page);
  window.gtag('event', 'page_view', { ...page, page_title: title });
  return true;
};

export const trackSignupPageView = () => sendOncePerSession(
  'serviti_analytics_signup_view',
  'signup_view',
  { signup_flow: 'self_serve_email_code' },
);

export const trackSignupCodeRequested = () => sendEvent(
  'signup_code_requested',
  { signup_flow: 'self_serve_email_code' },
);

// Our own team's signups (tests, demos) are not ad conversions.
export const isInternalEmail = (email) => /@([a-z0-9-]+\.)*serviti\.ai$/i.test(String(email || '').trim());

export const trackSignupConversion = (tenantId, email) => (isInternalEmail(email) ? false : sendOncePerSession(
  `serviti_analytics_sign_up:${tenantId || 'workspace'}`,
  // GA4's recommended signup event. Mark/import this event as the Google Ads
  // Sign-up conversion; custom funnel events remain diagnostic only.
  'sign_up',
  { method: 'email_code', signup_flow: 'self_serve' },
));

// First-touch campaign fields, kept from the first app page a visitor lands
// on so a signup several pages later still knows which ad brought them.
const ATTRIBUTION_KEY = 'serviti_attribution';
const ATTRIBUTION_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'gbraid', 'wbraid'];

export const rememberAttribution = (href = window.location.href, referrer = document.referrer) => {
  try {
    if (window.localStorage.getItem(ATTRIBUTION_KEY)) return;
    const url = new URL(href);
    const found = {};
    ATTRIBUTION_FIELDS.forEach((name) => {
      const value = url.searchParams.get(name);
      if (value) found[name] = value.slice(0, 200);
    });
    found.landing_path = cleanPageLocation(href).replace(/^https?:\/\/[^/]+/, '').slice(0, 200);
    try {
      const host = referrer ? new URL(referrer).hostname : '';
      if (host) found.referrer_host = host;
    } catch (_) {
      // An unparseable referrer is simply not recorded.
    }
    window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(found));
  } catch (_) {
    // Storage blocked: the signup still carries the GA client id.
  }
};

// A malformed cookie (bad %-escape) reads as absent rather than throwing.
const readCookie = (name) => {
  try {
    const match = String(document.cookie || '').split('; ').find((part) => part.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.slice(name.length + 1)) : '';
  } catch (_) {
    return '';
  }
};

// The GA client id ("GA1.1.123.456" -> "123.456") and the current session id
// from the property's own cookie (GS1.1.<sid>. or GS2.1.s<sid>$...). The
// server sends later milestones (trial activated, first payment) under them.
export const gaIdentifiers = () => {
  const out = {};
  const ga = readCookie('_ga').split('.');
  if (ga.length >= 4) out.ga_client_id = `${ga[2]}.${ga[3]}`;
  const session = readCookie(`_ga_${GOOGLE_TAG_ID.replace(/^G-/, '')}`);
  const sid = session.match(/^GS1\.\d\.(\d+)/) || session.match(/^GS2\.\d\.s(\d+)/);
  if (sid) out.ga_session_id = sid[1];
  return out;
};

// Built while the signup request is assembled, so it must never throw:
// analytics is optional, signup is not. Any failure means no attribution.
export const signupAttribution = () => {
  try {
    let stored = {};
    try {
      const parsed = JSON.parse(window.localStorage.getItem(ATTRIBUTION_KEY) || '{}');
      stored = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (_) {
      stored = {};
    }
    return { ...stored, ...gaIdentifiers() };
  } catch (_) {
    return {};
  }
};

export const trackOnboardingStarted = (tenantId) => sendOncePerSession(
  `serviti_analytics_onboarding_started:${tenantId || 'workspace'}`,
  'onboarding_started',
  { onboarding_flow: 'three_simple_steps' },
);
